<script>
import {get, post} from "@/utils/api";
import {MarkerClusterer} from '@googlemaps/markerclusterer';
import {gmapApi} from 'vue2-google-maps';
import Layout from "@/views/Layout.vue";
import SmartMultiSelect from "@/components/commons/SmartMultiSelect.vue";
import {AGENCY_SVG, getIcon, LOW_POTENTIAL_SVG, NO_PHONE_SVG, setColorByCA, STAR_SVG} from "@/utils/mapIcons";
import clipboard from "@/utils/clipboard";

export default {
  components: {
    SmartMultiSelect,
    Layout
  },
  data: () => ({
    searchform: {
      marketingAreaIds: [],
      networks: [],
      potential: [],
      logTypes: [],
      comparisonDate: null,
      customDate: null,
    },
    agencies: [],
    marketingAreas: [],
    networks: [],
    loading: false,
    clusters: null,
    infoWindow: null,
    isCustomDate: false,
    panel: 0
  }),
  mounted() {
    if (this.comparisonDates.length > 1) {
      this.searchform.comparisonDate = this.comparisonDates[1].value;
    }
  },
  async created() {
    await this.loadMarketingAreas();
    await this.getNetworks();
    this.search();
  },
  watch: {
    'searchform.comparisonDate'(newVal) {
      if (newVal === null) {
        this.isCustomDate = true;
        this.$nextTick(() => {
          const input = this.$refs.customPicker?.$el?.querySelector('input');
          input?.click();
        });
      } else {
        this.isCustomDate = false;
      }
    },
    'searchform.customDate'(newVal) {
      if (!newVal) {
        this.isCustomDate = false;
        this.searchform.comparisonDate = this.comparisonDates[1].value;
      }
    },
    markers() {
      this.updateClusterer();
    }
  },
  methods: {
    formatAgencyRow(a) {
      const address = [a.street, a.city, a.postalcode].map(el => el?.trim() ?? "").join(" ");
      const phone = (a.phone != null && a.phone.trim() !== "") ? a.phone.trim() : this.$t('map.noPhone');
      return [a.name, address, phone, a.email, `${window.location.origin}/hlagons/crm/${a.id}`].join("\t");
    },
    copyTooltip(agences) {
      const row = agences.map(a => this.formatAgencyRow(a)).join("\n");
      clipboard.copyTextToClipboard(row);
    },
    copyAll() {
      const headers = [this.$t('map.copyHeaders.name'), this.$t('map.copyHeaders.address'), this.$t('map.copyHeaders.phone'), this.$t('map.copyHeaders.email'), this.$t('map.copyHeaders.link')].join("\t") + "\n";

      const rows = this.agencies.map(a => this.formatAgencyRow(a)).join("\n");
      clipboard.copyTextToClipboard(headers + rows);
    },
    STAR_SVG,
    LOW_POTENTIAL_SVG,
    NO_PHONE_SVG,
    AGENCY_SVG,
    search() {
      if (!this.loading && this.searchform.comparisonDate){
        this.getAgencies();
      }
    },
    getAgencies() {
      this.loading = true
      const url = "/api/agency/frenchAgencies"
      const date = this.isCustomDate ? this.searchform.customDate : this.searchform.comparisonDate;

      const body = {
        comparisonDate: date,
        networkIds: this.searchform.networks,
        potentials: this.searchform.potential,
        marketingAreaIds: this.searchform.marketingAreaIds,
        logTypes: this.searchform.logTypes
      };

      return post(url, body)
          .json((data) => {
            this.agencies = data;
          })
          .then(() => {
            this.loading = false;
          });
    },
    loadMarketingAreas() {
      const url = "/api/search/marketingarea";
      return get(url)
          .json((data) => {
            // All Marketing Areas without Italy
            this.marketingAreas = data.filter(ma => ma.id != 4);
            this.searchform.marketingAreaIds = this.marketingAreas.map(ma => ma.id);
          })

    },
    getNetworks() {
      const url = "/api/search/networks";
      return get(url).json((data) => {
        this.networks = data;
      });
    },
    updateClusterer() {
      this.$refs.map.$mapPromise.then(map => {
        if (!this.infoWindow) {
          this.infoWindow = new this.google.maps.InfoWindow();
        }

        if (this.clusters) {
          this.clusters.clearMarkers();
        }

        const googleMarkers = this.markers.map(m => {
          const marker = new window.google.maps.marker.AdvancedMarkerElement({
            position: m.position,
            map,
            title: m.title,
            content: getIcon(m.agencies[0].potential, setColorByCA(m.agencies[0].currentCa, m.agencies[0].hasBeenConnected)),
          });

          marker.addListener("gmp-click", () => {
            const content = m.agencies.map((agency) => `
<div style="margin-bottom: 8px; padding-bottom: 8px; ${m.agencies.length > 1 ? 'border-bottom: 1px solid #eee;' : ''}">
<strong>${agency.name}</strong><br/>
${agency.street}<br/>
${agency.city}<br/>
${this.$tc('map.network', agency.networkIds.length)} : ${agency.networkIds.map(id => this.networksWithLabels.find(n => n.id === id)?.name).join(', ') || this.$t('map.potentials.notSpecified')}<br/>
${this.$t('map.event')} : ${agency.logType ? this.$t(`crm.logType.${agency.logType}`) : this.$t('map.none')}${agency.dateLogType ? `, ${this.$t('on').toLowerCase()} ${this._formatDate(this._parseDateISO(agency.dateLogType), this._datePattern.localDate)}` : ''} <br/>
<a href="/hlagons/crm/${agency.id}" target="_blank">${this.$t('map.seeAgency')}</a>
</div>
`).join('')

            const header = document.createElement('div');
            header.style.display = 'flex';
            header.style.alignItems = 'center';

            const icon = document.createElement('span');
            icon.className = 'mdi mdi-clipboard-outline';
            icon.style.fontSize = '18px';
            icon.style.cursor = 'pointer';
            icon.title = this.$t('map.copyAgency');
            icon.addEventListener('click', () => this.copyTooltip(m.agencies));

            header.appendChild(icon);
            this.infoWindow.setOptions({headerContent: header});

            this.infoWindow.setContent(`<div style="min-width: 180px">${content}</div>`)
            this.infoWindow.open(map, marker)
          })

          return marker;
        });

        this.clusters = new MarkerClusterer({map, markers: googleMarkers});
      });
    },
    clearSearchForm() {
      this.searchform = {
        networks: [],
        potential: [],
        marketingAreaIds: this.marketingAreas.map(ma => ma.id),
        logTypes: [],
        comparisonDate: this.comparisonDates[1].value,
        customDate: null
      }
      this.isCustomDate = false;
      this.search()
    },
  },
  computed: {
    comparisonDates() {
      return [
        {
          value: this._formatDate(this._subYears(new Date(), 2), this._datePattern.localDate),
          name: this.$tc('year', 2, {n: 2})
        },
        {
          value: this._formatDate(this._subYears(new Date(), 1), this._datePattern.localDate),
          name: this.$tc('year', 1, {n: 1})
        },
        {
          value: this._formatDate(this._subMonths(new Date(), 6), this._datePattern.localDate),
          name: this.$tc('month', 6, {n: 6})
        },
        {
          value: this._formatDate(this._subMonths(new Date(), 3), this._datePattern.localDate),
          name: this.$tc('month', 3, {n: 3})
        },
        {value: null, name: this.$t('map.otherDate')},
      ]
    },
    networksWithLabels() {
      return [...this.networks, {id: -1, name: this.$t('map.potentials.notSpecified')}]
    },
    legendIcons() {
      return [
        {svg: () => NO_PHONE_SVG(null, 'black', false), label: this.$t('map.potentials.noSolicit')},
        {svg: () => LOW_POTENTIAL_SVG(null, 'black', false), label: this.$t('map.potentials.lowPotential')},
        {svg: () => STAR_SVG(null, 'black', false), label: this.$t('map.potentials.highPotential')},
        {svg: () => AGENCY_SVG(null, 'black', false), label: this.$t('map.potentials.notSpecified')},
      ]
    },
    legendColors() {
      return [
        {color: 'green', label: `${this.$t('ca')} > 0 €`},
        {color: 'orange', label: this.$t('map.caption.connectedNoCa')},
        {color: 'red', label: this.$t('map.caption.notConnectedNoCa')},
      ]
    },
    potentials() {
      return [
        {id: 1, name: this.$t('map.potentials.noSolicit')},
        {id: 2, name: this.$t('map.potentials.lowPotential')},
        {id: 3, name: this.$t('map.potentials.highPotential')},
        {id: -1, name: this.$t('map.potentials.notSpecified')}
      ]
    },
    logTypes() {
      return [
        {name: this.$t('crm.logType.visit'), value: 'visit'},
        {name: this.$t('crm.logType.call'), value: 'call'},
        {name: this.$t('crm.logType.workshop'), value: 'workshop'},
        {name: this.$t('crm.logType.mail'), value: 'mail'},
        {name: this.$t('crm.logType.other'), value: 'other'},
        {name: this.$t('map.noEvents'), value: 'none'},
      ]
    },
    google: gmapApi,
    markers() {
      const groups = {}
      this.agencies.filter(f => f.latitude && f.longitude)
          .forEach((f) => {
            const key = `${f.latitude}_${f.longitude}`
            if (!groups[key]) groups[key] = []
            groups[key].push(f)
          })

      return Object.values(groups).map((agencies) => ({
        agencies,
        title: agencies.map(a => a.name).join(', '),
        position: {lat: agencies[0].latitude, lng: agencies[0].longitude}
      }))
    }
  }
}
</script>

<template>
  <layout :drawerShow="_isDesktop()" :drawerWidth="300">
    <template v-slot:nav-tools>
      <v-icon @click="copyAll()" class="mr-4" :title="$t('map.copyAllAgencies')">mdi-clipboard-outline</v-icon>
    </template>
    <template slot="drawer">
      <div class="d-flex flex-column" style="height: 100%;">

        <v-form
            ref="searchform"
            v-if="searchform"
            v-on:submit.prevent="search()"
        >

          <v-row justify="start" class="mt-3 mb-3">
            <v-col cols="12" class="pt-0">
              <smart-multi-select
                  :label="$t('map.networks')"
                  v-model="searchform.networks"
                  item-text="name"
                  item-value="id"
                  :items="networksWithLabels"
                  :default-select-all="false"
              />
            </v-col>

            <v-col cols="12" class="pt-0">
              <smart-multi-select
                  :label="$t('map.commercialZones')"
                  v-model="searchform.marketingAreaIds"
                  :items="marketingAreas"
                  item-text="name"
                  item-value="id"
                  :default-select-all="false"
              />
            </v-col>

            <v-col cols="12" class="pt-0">
              <smart-multi-select
                  :label="$t('map.potentials.label')"
                  v-model="searchform.potential"
                  :items="potentials"
                  item-text="name"
                  item-value="id"
                  :default-select-all="false"
              />
            </v-col>

            <v-col cols="12" class="pt-0">
              <smart-multi-select
                  :label="$t('map.events')"
                  v-model="searchform.logTypes"
                  item-text="name"
                  item-value="value"
                  :items="logTypes"
                  :default-select-all="false"
              />
            </v-col>

            <v-col cols="12" class="pt-0">
              <smart-picker
                  ref="customPicker"
                  v-if="isCustomDate"
                  v-model="searchform.customDate"
                  :label="$t('map.comparisonDate')"
                  :inputOpts="{ dense: true, clearable: true }"
              />
              <v-select
                  v-else
                  v-model="searchform.comparisonDate"
                  :items="comparisonDates"
                  item-value="value"
                  item-text="name"
                  :label="$t('map.comparisonDate')"
                  dense
                  hide-details
              >
              </v-select>
            </v-col>
          </v-row>

          <smart-btn
              :loading="loading"
              type="submit"
              block
              primary
              :disabled="loading">
            {{ $t('search') }}
          </smart-btn>
          <smart-btn
              class="mt-1"
              @click.native="clearSearchForm()"
              block
              tertiary
          >{{ $t('resetSearch') }}
          </smart-btn>
        </v-form>

        <v-expansion-panels class="mt-auto" style="border: 1px solid grey;" v-model="panel">
          <v-expansion-panel>
            <v-expansion-panel-header class="pa-2 font-weight-bold"
                                      style="font-size: 12px; min-height: 60px !important;">
              {{ $t('map.caption.label') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div style="font-size: 12px;">
                <div class="mb-1 font-weight-medium">{{ $t('map.colors') }} :</div>
                <div v-for="item in legendColors" :key="item.label" class="d-flex align-center mb-1">
                  <div class="mr-2"
                       :style="`width: 14px; height: 14px; background-color: ${item.color}; flex-shrink: 0;`"></div>
                  <span>{{ item.label }}</span>
                </div>

                <div class="mb-1 mt-2 font-weight-medium">{{ $t('map.icons') }} :</div>
                <div v-for="item in legendIcons" :key="item.label" class="d-flex align-center mb-1">
                  <div class="mr-2"
                       style="width: 16px; height: 16px; overflow: hidden; position: relative; flex-shrink: 0;">
                    <div style="position: absolute; top: -4px; left: -10px;" v-html="item.svg()"></div>
                  </div>
                  <span>{{ item.label }}</span>
                </div>

              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </template>
    <template slot="content">
      <v-container fluid>
        <v-col>
          <GmapMap
              ref="map"
              :options="{ isFractionalZoomEnabled: true, mapId: 'DEMO_MAP_ID' }"
              :center="{ lat: 46, lng: 3 }"
              :zoom="6"
              style="width: 100%; height: 800px; margin: auto;"
          />
        </v-col>
      </v-container>
    </template>
  </layout>
</template>